import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import {
  ApprovalRule,
  Policy,
  RequestorRule,
  ResourceRule,
} from "@p0security/shared/types/policy/types";
import {
  Button,
  type FilterValue,
  Modal,
  Switch,
  Table,
  Tooltip,
} from "@p0security/ui";
import { nonNullable } from "@p0security/util/nonNullable";
import {
  CellAction,
  StyledTableContainer,
} from "components/Policies/common/TablePrimitives";
import {
  buildMultiFilterColumnProps,
  buildTextFilterColumnProps,
} from "components/Policies/common/tableFilterColumns";
import { usePagination } from "hooks/usePagination";
import stringify from "json-stable-stringify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrayParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { usePolicyStore } from "../../../../store/policyStore.ts";
import { NamePreview } from "../components/previews/NamePreview.tsx";
import { RequestorPreview } from "../components/previews/RequestorPreview.tsx";
import { ResourcePreview } from "../components/previews/ResourcePreview.tsx";
import { useSubmitWorkflow } from "../useSubmitWorkflow.ts";
import {
  getResourceFilters,
  getUniqueResources,
  hashRule,
  useOnFilter,
} from "../utils.ts";
import { RulePreviewDrawer } from "./RulePreviewDrawer.tsx";
import { ApprovalSummary } from "./common.tsx";

const { confirm } = Modal;

type Props = {
  canSubmit: boolean;
  handleDelete: (record: Policy) => void;
  /** When true, hides the enable/disable and delete columns and disables edit
   *  navigation, rendering the policies as a read-only listing. */
  readOnly?: boolean;
  /** Overrides the policies sourced from the store, e.g. to display a filtered
   *  subset. Defaults to the full set of loaded rules. */
  rules?: Policy[];
};

const PolicyTableView: React.FC<Props> = ({
  canSubmit,
  handleDelete,
  readOnly = false,
  rules,
}) => {
  const {
    currentVersion,
    isLoading,
    jsonRules: storeRules,
    setErrors,
    setSelectedRule,
    setShowConfigRulesModal,
  } = usePolicyStore();
  const jsonRules = rules ?? storeRules;
  const submitWorkflow = useSubmitWorkflow();
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const [pagination, setPagination] = usePagination();

  const [previewRule, setPreviewRule] = useState<Policy | null>(null);

  const [disablingIndex, setDisablingIndex] = useState<number | undefined>(
    undefined
  );

  const rowClassName = useCallback(
    (record: Policy) => (!!record.disabled ? "disabled-rule" : ""),
    []
  );

  // Table filters
  const uniqueResources = useMemo(
    () => getUniqueResources(jsonRules),
    [jsonRules]
  );
  const resourceFilters = useMemo(
    () => getResourceFilters(uniqueResources),
    [uniqueResources]
  );
  const disabledFilters = useMemo(
    () => [
      {
        text: "Enabled",
        value: "false",
      },
      {
        text: "Disabled",
        value: "true",
      },
    ],
    []
  );
  const [searchParams] = useSearchParams();

  const [filterQuery, setFilterQuery] = useQueryParams({
    name: withDefault(StringParam, null),
    identity: withDefault(StringParam, null),
    actions: withDefault(StringParam, null),
    resource: ArrayParam,
    disabled: ArrayParam,
  });

  const setters = useMemo(() => {
    const setTextFilter =
      (key: "actions" | "identity" | "name") => (value: string | null) =>
        setFilterQuery({ [key]: value }, "replaceIn");
    return {
      name: setTextFilter("name"),
      identity: setTextFilter("identity"),
      actions: setTextFilter("actions"),
    };
  }, [setFilterQuery]);

  const handleFilterChange = useCallback(
    (_pagination: unknown, filters: Record<string, FilterValue | null>) => {
      setFilterQuery(
        {
          name: (filters.name?.[0] as string | undefined) ?? null,
          identity: (filters.requestor?.[0] as string | undefined) ?? null,
          actions: (filters.actions?.[0] as string | undefined) ?? null,
          resource: (filters.resource ?? []).map(String),
          disabled: (filters.disabled ?? []).map(String),
        },
        "replaceIn"
      );
    },
    [setFilterQuery]
  );

  // Reset to page 1 whenever a column filter changes, so a stale page number
  // (e.g. page 5) can't outrun a freshly-filtered, shorter result set.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setPagination({ page: 1, pageSize: pagination.pageSize ?? 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const updatePagination = useCallback(
    (page: number, pageSize: number) => setPagination({ page, pageSize }),
    [setPagination]
  );

  // Open edit modal for a new rule if there is a rule specified in the YAML
  useEffect(() => {
    const yamlStr = searchParams.get("yaml");
    if (!yamlStr) return;
    setErrors([]);
    setShowConfigRulesModal(true);
  }, [searchParams, setErrors, setShowConfigRulesModal, setSelectedRule]);

  const handleClosePreview = useCallback(() => setPreviewRule(null), []);

  const setRuleDisabled = useCallback(
    async (record: Policy, rowIndex: number, disabled: boolean) => {
      // Caution: rowIndex refers to the rendered table listing policies, NOT the actual backing
      // list of policies. They may differ in some situations; for example, when the table is filtered.
      setDisablingIndex(rowIndex);

      const updatedRules = [...jsonRules];
      const newRecord = { ...record, disabled };

      const existingRuleHash = stringify(record);
      const index = updatedRules.findIndex(
        (rule) => stringify(rule) === existingRuleHash
      );
      if (index !== -1) {
        updatedRules[index] = newRecord;
      } else {
        // This should never happen
        console.error("Rule not found");
        setDisablingIndex(undefined);
        return;
      }

      const policyName = record.name ? `Policy "${record.name}"` : "Policy";
      const action = disabled ? "disabled" : "enabled";
      await submitWorkflow({
        jsonData: { currentVersion, policy: { rules: updatedRules } },
        shouldNavigate: false,
        successMessage: `${policyName} was successfully ${action}.`,
      });

      setDisablingIndex(undefined);
    },
    [currentVersion, jsonRules, submitWorkflow]
  );

  ////////////////////////////////////////////////////////////////////
  // TODO: This code is largely duplicated in BundleList.tsx        //
  //                                                                //
  // DRY for reusability. In the meantime you MUST make coordinated //
  // changes in order to preserve UI consistency!                   //
  ////////////////////////////////////////////////////////////////////
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...buildTextFilterColumnProps<Policy>({
        value: filterQuery.name,
        onChange: setters.name,
        onFilter: useOnFilter("name"),
      }),
      render: (name: string | undefined) => (
        <div className="min-w-40">
          <NamePreview name={name} />
        </div>
      ),
    },
    {
      title: "Identity",
      dataIndex: "requestor",
      key: "requestor",
      ...buildTextFilterColumnProps<Policy>({
        value: filterQuery.identity,
        onChange: setters.identity,
        onFilter: useOnFilter("requestor"),
      }),
      render: (requestor: RequestorRule) => (
        <div className="min-w-70">
          <RequestorPreview requestor={requestor} />
        </div>
      ),
    },
    {
      title: "Resource",
      dataIndex: "resource",
      key: "resource",
      ...buildMultiFilterColumnProps<Policy>({
        filteredValue: (filterQuery.resource ?? []).filter(
          (v): v is string => v !== null
        ),
        options: resourceFilters,
        onFilter: (value, record: any) => record.resource.type === value,
      }),
      render: (resource: ResourceRule) => (
        <ResourcePreview resource={resource} />
      ),
    },
    {
      title: "Actions",
      dataIndex: "approval",
      key: "actions",
      ...buildTextFilterColumnProps<Policy>({
        value: filterQuery.actions,
        onChange: setters.actions,
        onFilter: useOnFilter("approval"),
      }),
      render: (record: ApprovalRule[]) => <ApprovalSummary record={record} />,
    },
    {
      title: "Enabled", // We want to present it as "Enabled" in the table
      dataIndex: "disabled", // ...But the actual underlying value we're operating on is the `disabled` field
      key: "disabled",
      ...buildMultiFilterColumnProps<Policy>({
        filteredValue: (filterQuery.disabled ?? []).filter(
          (v): v is string => v !== null
        ),
        options: disabledFilters,
        onFilter: (value, record) => (value === "true") === !!record.disabled,
      }),
      render: (value: boolean | undefined, record: Policy, index: number) => (
        <CellAction>
          <Tooltip title="Toggle access policy. Disabled policies are not evaluated as part of requests.">
            <Switch
              value={!(value ?? false)}
              // Memoized by antd Table
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(checked) => setRuleDisabled(record, index, !checked)}
              disabled={disablingIndex !== undefined}
              loading={disablingIndex === index}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Tooltip>
        </CellAction>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (_: any, record: any) => {
        return canSubmit ? (
          <CellAction>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              // Memoized by antd Table
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => {
                confirm({
                  title: "Are you sure you want to delete this policy?",
                  content: "This action cannot be undone.",
                  onOk() {
                    handleDelete(record);
                  },
                  okText: "Yes, delete policy",
                  okType: "danger",
                });
              }}
            />
          </CellAction>
        ) : null;
      },
    },
  ];

  // In read-only mode, omit the delete column but keep the switch visible.
  const visibleColumns = readOnly
    ? columns.filter((c) => c.key !== "action")
    : columns;

  const stringifyRecord = useCallback(
    (record: Policy) => nonNullable(stringify(record)),
    []
  );

  const handleRowClick = useCallback(
    (record: Policy) => ({
      onClick: () => {
        if (canSubmit && !readOnly) {
          navigateWithSearchParams(`edit/${hashRule(record)}`);
        } else {
          setPreviewRule(record);
        }
      },
    }),
    [canSubmit, readOnly, navigateWithSearchParams]
  );

  return (
    <StyledTableContainer>
      <Table
        columns={visibleColumns}
        dataSource={jsonRules}
        loading={isLoading}
        rowKey={stringifyRecord}
        onRow={handleRowClick}
        onChange={handleFilterChange}
        pagination={{
          current: pagination.page,
          defaultCurrent: 1,
          pageSize: pagination.pageSize,
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: updatePagination,
        }}
        rowClassName={rowClassName}
      />
      <RulePreviewDrawer rule={previewRule} onClose={handleClosePreview} />
    </StyledTableContainer>
  );
};

export default PolicyTableView;
